export type TicketStatus = "In-Progress" | "Pending Information" | "Resolved" | "Closed";
export type TicketCategory = "Complaint" | "Request" | "Query";

// ─── Reason / Rationale dropdown values ───────────────────────────────────────
// Shared because they are submitted as the first line of a ticket description / conversation
// message, and the thread recognises that line to render it in bold (see ThreadMessages).
// Adding an option to a dropdown without adding it here just means it won't be emphasised.

export const REOPEN_REASONS = [
  "Unsatisfactory response",
  "I want to submit additional details/documents",
  "My issue is not resolved",
];

export const CALLBACK_REASONS = [
  "I have an urgent request",
  "I want to have a discussion regarding my request",
];

export const ALL_TICKET_REASONS = [...REOPEN_REASONS, ...CALLBACK_REASONS];

export interface ApiTicketItem {
  // Nullable in practice — the listing sends null for tickets with no descriptive type.
  typeOfTicket: string | null;
  type?: string;
  escalatedFrom: string | null;
  escalatedTo: string | null;
  category: string | null;
  ticketNumber: number;
  dateAndTime: string;
  // Null whenever the CRM has no due date set — see formatApiDate, which renders that as blank
  // instead of the Unix epoch.
  dueDate: string | null;
  status: number;
  lan: string;
  req_id: number;
}

export const API_STATUS_MAP: Record<number, TicketStatus> = {
  2: "In-Progress",
  7: "In-Progress",
  11: "In-Progress",
  10: "In-Progress",
  4: "Resolved",
  8: "Closed",
  9: "Closed",
  5: "Closed",
  6: "Pending Information",
  9001: "Closed",
};

// Every known ticket status — sent when the listing shouldn't be narrowed to one tab.
export const ALL_TICKET_STATUS_CODES = [2, 6, 4, 5, 7, 10, 8, 9, 11,9001,9002];

export const TAB_TO_API_STATUS: Partial<Record<string, number[]>> = {
  "In-Progress": [2, 7, 11, 10,9002],
  "Pending Information": [6],
  "Resolved": [4],
  "Closed": [8, 9, 5, 9001],
};

export interface ApiConversationAttachment {
  name: string;
  url: string;
}

export interface ApiConversation {
  sender: string;
  text: string;
  attachments: ApiConversationAttachment[];
  createdAt: string;
}

export interface ApiTicketDetails {
  category: string;
  ticketNumber: number;
  accountNumber?: string;
  status?: string;
  esclatedTo?: string | null;
  escalatedFrom: string | null;
  subject: string;
  description: string;
  CrmType: string;
  type: string;
  createdAt: string;
  attachemnts: ApiConversationAttachment[];
  conversations: ApiConversation[];
  // Number of conversation entries in this page. Ticket Details offers "View more" only while it
  // equals CONVERSATION_PAGE_SIZE — a full page implies another may follow. Optional because the
  // API doesn't return it yet; until it does, the button simply never appears.
  convoCount?: number;
}

export interface ThreadMessage {
  role: "customer" | "support";
  authorName?: string;
  date: string;
  text: string;
}

export interface TicketThread {
  id: string;
  reference: string;
  title: string;
  messages: ThreadMessage[];
}

export interface Ticket {
  typeOfTicket?: string;
  type?: string;
  id: string;
  subject: string;
  category: TicketCategory;
  subCategory?: string;
  ticketNumber: string;
  referenceId?: string;
  loanAccountNumber?: string;
  escalatedFrom?: string | null;
  escalatedTo?: string | null;
  status: TicketStatus;
  dateTime: string;
  dueDate: string;
  raisedOn: string;
  raisedOnIso?: string;
  description?: string;
  avanceResponse?: string;
  attachments?: string[];
  statusMessage?: string;
  threads?: TicketThread[];
}

export const STATUS_COLOR: Record<TicketStatus, string> = {
  "In-Progress": "#F59E0B",
  "Pending Information": "#3B82F6",
  Resolved: "#10B981",
  Closed: "#9CA3AF",
};

export const CATEGORY_STYLE: Record<TicketCategory, { bg: string; color: string }> = {
  Complaint: { bg: "#FEE2E2", color: "#DC2626" },
  Request: { bg: "#FEF3C7", color: "#D97706" },
  Query: { bg: "#DBEAFE", color: "#2563EB" },
};
